import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, useFormikContext } from 'formik';
import { policyType } from '@ourbranch/lookups';

import withDatePicker from 'core/components/with-date-picker';
import { useStore } from 'core/store';
import { DisableProvider } from 'common/disabled-context';
import { Collapsible } from '../collapsible';
import { MoreInfoNeededFlagItem } from '../add-info-flag-item';
import { propDetailsCodes } from '../quote-forms/quote-forms.constants';
import { ErrorAlert } from '../quote-errors';
import { Condo } from './condo';
import { Home } from './home';
import { InitialRiskDetail } from '../initial-risk-detail';
import { HintFlagItem } from '../hint-flag-item';

const PropertyDetail = ({ showPropertyDetailsCallout, sectionsToShow }) => {
  const { values, isSubmitting, errors } = useFormikContext();
  const {
    quote: { getAlerts }
  } = useStore();

  const propertyErrors = useRef(null);
  const [shouldShow, setShouldShow] = useState(false);
  const hasCondo = [policyType.CABundle, policyType.Condo].includes(values.policyType);
  const hasFarm = [policyType.Farm].includes(values.policyType) && !!values.address && !!values.state;

  const title = hasFarm ? 'Initial Risk Detail' : 'Property Detail';

  const relevantAlerts = getAlerts(propDetailsCodes, values.policyType);
  const anyAlerts = getAlerts().length > 0;

  useEffect(() => {
    if (relevantAlerts.length && propertyErrors.current && isSubmitting) {
      propertyErrors.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [relevantAlerts, isSubmitting]);

  const formikErrors = errors && (errors.home || errors.condo || errors.farm);
  const forceOpen = showPropertyDetailsCallout || relevantAlerts.length > 0 || formikErrors || hasFarm;

  useEffect(() => {
    if (formikErrors) {
      setShouldShow(true);
    }
    if (sectionsToShow && sectionsToShow?.length > 0) {
      const shouldBeShown = propDetailsCodes.every((element) => sectionsToShow.includes(element));
      setShouldShow(shouldBeShown);
    }
    if (hasFarm) {
      setShouldShow(true);
    }
  }, [formikErrors, sectionsToShow, hasFarm]);

  return (
    <Form>
      {hasFarm && !formikErrors && !anyAlerts && (
        <HintFlagItem message="Then add the first risk on that location (typically a home)" />
      )}
      {(values.isNewConstruction || formikErrors) && <MoreInfoNeededFlagItem />}
      {relevantAlerts.length > 0 && (
        <ErrorAlert errors={relevantAlerts} ref={propertyErrors} id="error-alert-property-details" />
      )}
      {(values.isNewConstruction || shouldShow) && (
        <Collapsible title={title} forceOpen={forceOpen} initialOpen={relevantAlerts.length > 0 || hasFarm}>
          {hasCondo && <Condo />}
          {hasFarm && <InitialRiskDetail />}
          {!hasCondo && !hasFarm && (
            <DisableProvider>
              <Home />
            </DisableProvider>
          )}
        </Collapsible>
      )}
    </Form>
  );
};

PropertyDetail.propTypes = {
  sectionsToShow: PropTypes.array,
  showPropertyDetailsCallout: PropTypes.bool.isRequired
};

PropertyDetail.defaultProps = {
  sectionsToShow: []
};

export default withDatePicker(PropertyDetail);
